import type { NodeTypeDef } from '../core/types';

/**
 * Generators: nodes with no inputs that synthesise a pattern from parameters.
 *
 * Every generator tiles seamlessly. Scale parameters are integers precisely
 * because that is what makes the underlying lattice wrap at the UV boundary —
 * a fractional cell count would leave a visible seam on the mesh.
 */
export const generators: NodeTypeDef[] = [
  {
    id: 'gen.uniform',
    label: 'Uniform Color',
    category: 'generator',
    description: 'A flat, constant colour or value.',
    guidance:
      'The workhorse constant. Use as a base layer to blend onto, as a solid roughness or metallic value, or to tint a mask via Blend.',
    keywords: ['solid', 'constant', 'fill', 'flat', 'color'],
    inputs: [],
    output: 'color',
    params: [
      {
        name: 'color',
        label: 'Color',
        type: 'color',
        default: [0.5, 0.5, 0.5, 1],
        help: 'Linear-space RGBA.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) { return p_color; }
    `,
  },

  {
    id: 'gen.noise',
    label: 'Noise',
    category: 'generator',
    description: 'Tiling fractal noise: Perlin, value, or ridged.',
    guidance:
      'The single most useful generator. Perlin for organic softness, value for grain and clouds, ridged for mountains, bark, and erosion creases. Raise Octaves for detail; lower Gain for a smoother, more billowy result.',
    keywords: ['perlin', 'fbm', 'fractal', 'clouds', 'grunge', 'random', 'organic'],
    inputs: [],
    output: 'grayscale',
    params: [
      {
        name: 'type',
        label: 'Type',
        type: 'enum',
        default: 'perlin',
        options: [
          { value: 'perlin', label: 'Perlin' },
          { value: 'value', label: 'Value' },
          { value: 'ridged', label: 'Ridged' },
        ],
      },
      {
        name: 'scale',
        label: 'Scale',
        type: 'int',
        default: 4,
        min: 1,
        max: 64,
        help: 'Base feature count across the tile. Integer, so the result wraps.',
      },
      { name: 'octaves', label: 'Octaves', type: 'int', default: 5, min: 1, max: 10 },
      {
        name: 'lacunarity',
        label: 'Lacunarity',
        type: 'float',
        default: 2,
        min: 1,
        max: 4,
        step: 0.01,
        group: 'Fractal',
        help: 'Frequency multiplier between octaves.',
      },
      {
        name: 'gain',
        label: 'Gain',
        type: 'float',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Fractal',
        help: 'Amplitude falloff between octaves. Lower is smoother.',
      },
      { name: 'seed', label: 'Seed', type: 'int', default: 0, min: 0, max: 9999 },
      {
        name: 'scaleY',
        label: 'Scale Y',
        type: 'int',
        default: 0,
        min: 0,
        max: 64,
        group: 'Shape',
        help: 'Separate vertical feature count. 0 keeps features square; raising it stretches them horizontally.',
      },
      {
        name: 'warp',
        label: 'Warp',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Shape',
        help: 'Domain warp: the noise displaces itself, producing curled, flow-like structure no octave count can imitate.',
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'vec2',
        default: [0, 0],
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Shape',
        help: 'Pans the pattern. Useful for decorrelating two noises that share a seed.',
      },
      {
        name: 'contrast',
        label: 'Contrast',
        type: 'float',
        default: 1,
        min: 0,
        max: 4,
        step: 0.01,
        group: 'Output',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 period = vec2(float(p_scale), float(p_scaleY > 0 ? p_scaleY : p_scale));
        vec2 p = (uv + p_offset) * period;
        float seed = float(p_seed);

        // Domain warp: displace the sample point by a low-frequency copy of the
        // noise itself. Two extra lookups buy curled, flowing structure that
        // simply stacking octaves cannot produce.
        if (p_warp > 0.0001) {
          vec2 warpPeriod = max(floor(period * 0.5), vec2(1.0));
          float wx = perlin(uv * warpPeriod, warpPeriod, seed + 91.7);
          float wy = perlin(uv * warpPeriod, warpPeriod, seed + 137.3);
          p += vec2(wx, wy) * p_warp * period * 0.5;
        }

        float n;
        if (p_type == K_type_perlin) {
          n = fbmPerlin(p, period, seed, p_octaves, p_lacunarity, p_gain) * 0.5 + 0.5;
        } else if (p_type == K_type_value) {
          n = fbmValue(p, period, seed, p_octaves, p_lacunarity, p_gain);
        } else {
          n = fbmRidged(p, period, seed, p_octaves, p_lacunarity, p_gain);
        }
        n = saturate((n - 0.5) * p_contrast + 0.5);
        if (p_invert) n = 1.0 - n;
        return vec4(vec3(n), 1.0);
      }
    `,
  },

  {
    id: 'gen.cellular',
    label: 'Cellular',
    category: 'generator',
    description: 'Tiling Worley and Voronoi: blobs, vein networks, and flat cells.',
    guidance:
      'Reach for this for stone, scales, cracked mud, leather, and foam. All four modes scatter one point per cell and differ only in what they measure from it. Worley is the distance to that point, so it reads as blobs or bumps — the classic cellular noise. Voronoi draws the borders between those cells as flat black lines on flat white, with Vein Width setting the thickness. It is a mask, not a height map — for grout or dried mud, run it through Distance in Inside mode, which turns the flat plates into the sloped falloff a groove needs and does it for any mask rather than only this one. Flat Voronoi is the diagram itself, one flat random value per cell, which is what you want when the cells need to be told apart rather than shaded.',
    keywords: [
      'worley',
      'voronoi',
      'cells',
      'stone',
      'crack',
      'vein',
      'scales',
      'organic',
      'f1',
      'f2',
    ],
    inputs: [],
    output: 'grayscale',
    params: [
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'worley',
        options: [
          { value: 'worley', label: 'Worley' },
          { value: 'worley2', label: 'Worley (2nd Nearest)' },
          { value: 'voronoi', label: 'Voronoi' },
          { value: 'flat', label: 'Flat Voronoi' },
        ],
      },
      { name: 'scale', label: 'Scale', type: 'int', default: 6, min: 1, max: 64 },
      {
        name: 'scaleY',
        label: 'Scale Y',
        type: 'int',
        default: 0,
        min: 0,
        max: 64,
        help: 'Separate vertical cell count. 0 keeps cells square.',
      },
      {
        name: 'metric',
        label: 'Metric',
        type: 'enum',
        default: 'euclidean',
        options: [
          { value: 'euclidean', label: 'Euclidean (round)' },
          { value: 'manhattan', label: 'Manhattan (diamond)' },
          { value: 'chebyshev', label: 'Chebyshev (square)' },
        ],
        help: 'Changes the cell shape outright — round stone, diamond scales, or square tiles.',
      },
      {
        name: 'smoothness',
        label: 'Smoothness',
        type: 'float',
        default: 0,
        min: 0,
        max: 0.5,
        step: 0.001,
        help: 'Rounds the ridge where cells meet, softening cracks into organic seams.',
      },
      {
        name: 'jitter',
        label: 'Jitter',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.01,
        help: '0 is a regular grid, 1 is fully random placement.',
      },
      { name: 'seed', label: 'Seed', type: 'int', default: 0, min: 0, max: 9999 },
      /**
       * Two controls rather than one, because the two families of mode want
       * genuinely different numbers for the same idea.
       *
       * Both say "how far, in cell widths, does the field take to reach white",
       * but a Worley blob ramps across most of its cell while a vein wants a
       * twentieth of one. Sharing a control meant sharing a default, and the
       * default belonged to Worley — which is why Voronoi came out as a soft
       * gradient filling every cell instead of the crack network it is named
       * for. Splitting them lets each arrive looking like itself.
       */
      {
        name: 'falloff',
        label: 'Falloff',
        type: 'float',
        default: 0.71,
        min: 0.02,
        max: 2,
        step: 0.01,
        group: 'Output',
        visibleWhen: (p) => p.mode === 'worley' || p.mode === 'worley2',
        help: 'Distance the blob takes to ramp to white, in cell widths.',
      },
      {
        name: 'veinWidth',
        label: 'Vein Width',
        type: 'float',
        default: 0.05,
        min: 0.005,
        max: 1,
        step: 0.005,
        group: 'Output',
        visibleWhen: (p) => p.mode === 'voronoi',
        help: 'Thickness of the lines between cells, as a fraction of a cell. Uniform under the Euclidean metric; the other two have no straight border to measure against, so their lines still vary.',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    shader: /* glsl */ `
      /**
       * The borders between cells, drawn as lines of one width.
       *
       * Thresholded rather than scaled. Dividing by a width is what the node
       * used to do and it never stops being a ramp — narrowing the width only
       * narrows the ramp, so the veins kept the grey shoulders that made the
       * mode read as a gradient rather than a network of edges.
       *
       * The threshold is applied to a real distance to the border, not to
       * F2 − F1, so Vein Width is a width: the line measures that fraction of a
       * cell across, everywhere, instead of thinning and thickening with the
       * angle between whichever two points happen to bound it.
       *
       * The transition either side is one texel, which is antialiasing rather
       * than shading — without it the lines crawl with jaggies at every angle.
       */
      float voronoiEdge(vec3 w, vec2 uv, vec2 period) {
        float dist;
        if (p_metric == K_metric_euclidean) {
          dist = worleyBorderDistance(uv * period, period, float(p_seed), p_jitter);
        } else {
          // A Manhattan or Chebyshev border is not a straight bisector, so
          // there is no closed form to measure against. F2 − F1 stands in,
          // halved because it closes at roughly twice the rate of a distance,
          // and the lines vary in width as they always did.
          dist = (w.y - w.x) * 0.5;
        }

        // The transition is one texel, expressed in the cell space the
        // distance is measured in. It antialiases the requested line without
        // becoming a minimum physical width: Vein Width remains a cell ratio
        // when the document resolution changes.
        vec2 cellTexel = uTexel * period;
        float aa = 0.5 * max(cellTexel.x, cellTexel.y);
        float halfWidth = p_veinWidth * 0.5;
        return smoothstep(halfWidth - aa, halfWidth + aa, dist);
      }

      vec4 texdir(vec2 uv) {
        vec2 period = vec2(float(p_scale), float(p_scaleY > 0 ? p_scaleY : p_scale));
        vec3 w = worleyCellsEx(
          uv * period, period, float(p_seed), p_jitter, p_metric, p_smoothness
        );
        // A Worley distance has to be divided by the span it should cover before
        // it lands in [0,1]. Flat Voronoi carries a cell id rather than a
        // distance, so it is already normalised and takes no control at all.
        float v;
        if (p_mode == K_mode_worley) v = saturate(w.x / max(p_falloff, 1e-4));
        else if (p_mode == K_mode_worley2) v = saturate(w.y / max(p_falloff, 1e-4));
        else if (p_mode == K_mode_voronoi) v = voronoiEdge(w, uv, period);
        else v = w.z;
        if (p_invert) v = 1.0 - v;
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'gen.gradient',
    label: 'Gradient',
    category: 'generator',
    description: 'Linear, radial, angular, or diamond gradient ramp.',
    guidance:
      'Use as a mask to fade an effect across the tile, as an input to Gradient Map, or as the driver for Warp. Angular mode is the standard way to build a swept or brushed-metal look.',
    keywords: ['ramp', 'linear', 'radial', 'angular', 'sweep', 'falloff'],
    inputs: [],
    output: 'grayscale',
    params: [
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'linear',
        options: [
          { value: 'linear', label: 'Linear' },
          { value: 'radial', label: 'Radial' },
          { value: 'angular', label: 'Angular' },
          { value: 'diamond', label: 'Diamond' },
          { value: 'square', label: 'Square' },
        ],
      },
      {
        name: 'rotation',
        label: 'Rotation',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
        help: 'In turns: 0.25 is a quarter rotation.',
      },
      {
        name: 'center',
        label: 'Center',
        type: 'vec2',
        default: [0.5, 0.5],
        min: -1,
        max: 2,
        step: 0.001,
      },
      {
        name: 'scale',
        label: 'Scale',
        type: 'float',
        default: 1,
        min: 0.01,
        max: 8,
        step: 0.01,
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Output',
      },
      {
        name: 'repeat',
        label: 'Repeat',
        type: 'int',
        default: 1,
        min: 1,
        max: 32,
        group: 'Output',
        help: 'Tiles the ramp. Values above 1 stay seamless.',
      },
      {
        name: 'pingpong',
        label: 'Ping-pong',
        type: 'bool',
        default: false,
        group: 'Output',
        help: 'Mirrors each repeat so the ramp has no hard reset.',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 c = uv - p_center;
        c = rot(p_rotation * TAU) * c;
        float v;
        if (p_mode == K_mode_linear) v = c.x * p_scale + 0.5;
        else if (p_mode == K_mode_radial) v = length(c) * 2.0 * p_scale;
        else if (p_mode == K_mode_angular) v = (atan(c.y, c.x) / TAU + 0.5) * p_scale;
        else if (p_mode == K_mode_diamond) v = (abs(c.x) + abs(c.y)) * 2.0 * p_scale;
        else v = max(abs(c.x), abs(c.y)) * 2.0 * p_scale;

        v = v + p_offset;
        v = v * float(p_repeat);
        if (p_pingpong) {
          float m = mod(v, 2.0);
          v = min(m, 2.0 - m);
        } else {
          v = fract(v);
        }
        if (p_invert) v = 1.0 - v;
        return vec4(vec3(saturate(v)), 1.0);
      }
    `,
  },

  {
    id: 'gen.shape',
    label: 'Shape',
    category: 'generator',
    description: 'A single antialiased primitive: disc, box, polygon, or star.',
    guidance:
      'The atom of pattern building. Feed a Shape into Tile Sampler to scatter it, or into Transform to place it. Falloff turns the hard silhouette into a soft gradient suitable for a height map.',
    keywords: ['circle', 'disc', 'box', 'square', 'polygon', 'star', 'primitive'],
    inputs: [],
    output: 'grayscale',
    params: [
      {
        name: 'shape',
        label: 'Shape',
        type: 'enum',
        default: 'circle',
        options: [
          { value: 'circle', label: 'Disc' },
          { value: 'box', label: 'Box' },
          { value: 'ngon', label: 'Polygon' },
          { value: 'star', label: 'Star' },
          { value: 'capsule', label: 'Capsule' },
          { value: 'cross', label: 'Cross' },
        ],
      },
      { name: 'size', label: 'Size', type: 'float', default: 0.4, min: 0, max: 1.5, step: 0.001 },
      {
        name: 'aspect',
        label: 'Aspect',
        type: 'float',
        default: 1,
        min: 0.05,
        max: 8,
        step: 0.01,
        help: 'Width over height.',
      },
      {
        name: 'rotation',
        label: 'Rotation',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
      },
      {
        name: 'center',
        label: 'Center',
        type: 'vec2',
        default: [0.5, 0.5],
        min: -1,
        max: 2,
        step: 0.001,
      },
      {
        name: 'sides',
        label: 'Sides / Points',
        type: 'int',
        default: 6,
        min: 3,
        max: 24,
        visibleWhen: (p) => p.shape === 'ngon' || p.shape === 'star',
      },
      {
        name: 'inset',
        label: 'Star Inset',
        type: 'float',
        default: 0.45,
        min: 0.05,
        max: 1,
        step: 0.01,
        visibleWhen: (p) => p.shape === 'star',
      },
      {
        name: 'roundness',
        label: 'Roundness',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.001,
        group: 'Shape',
        help: 'Rounds the corners. Has no effect on a disc.',
      },
      {
        name: 'outline',
        label: 'Outline',
        type: 'float',
        default: 0,
        min: 0,
        max: 0.5,
        step: 0.001,
        group: 'Shape',
        help: 'Hollows the shape, keeping a ring of this thickness. 0 stays solid.',
      },
      {
        name: 'falloffCurve',
        label: 'Falloff Curve',
        type: 'enum',
        default: 'smooth',
        options: [
          { value: 'linear', label: 'Linear (cone)' },
          { value: 'smooth', label: 'Smooth' },
          { value: 'sphere', label: 'Spherical (dome)' },
          { value: 'custom', label: 'Custom curve' },
        ],
        group: 'Output',
        help: 'Spherical gives a true hemisphere, which is what a rounded pebble or rivet needs. Custom hands the profile to the curve editor below.',
        visibleWhen: (p) => Number(p.falloff) > 0.0001,
      },
      {
        name: 'falloffProfile',
        label: 'Falloff Profile',
        type: 'curve',
        // Identity: the ramp passes through unchanged until the user shapes it.
        // Input is the distance ramp — 0 at the silhouette, 1 at the core — and
        // output is the height there, so the curve reads as a cross-section of
        // the shape from edge to centre.
        default: [
          { x: 0, y: 0 },
          { x: 1, y: 1 },
        ],
        group: 'Output',
        help: 'Cross-section from the outline (left) to the centre (right). Flatten the right side for a plateau, steepen the left for an undercut.',
        visibleWhen: (p) => Number(p.falloff) > 0.0001 && p.falloffCurve === 'custom',
      },
      {
        name: 'falloff',
        label: 'Falloff',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.001,
        group: 'Output',
        help: 'Softens the edge inward. 0 is a hard antialiased silhouette.',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 p = uv - p_center;
        p = rot(p_rotation * TAU) * p;
        p.x /= max(p_aspect, 0.001);

        // Roundness shrinks the primitive and then dilates the field back out,
        // which is what actually rounds a corner rather than just blurring it.
        float round = p_roundness * p_size * 0.5;
        float body = max(p_size - round, 1e-4);

        float d;
        if (p_shape == K_shape_circle) d = sdCircle(p, p_size);
        else if (p_shape == K_shape_box) d = sdBox(p, vec2(body)) - round;
        else if (p_shape == K_shape_ngon) d = sdNgon(p, body, float(p_sides), 0.0) - round;
        else if (p_shape == K_shape_star) d = sdStar(p, p_size, float(p_sides), p_inset);
        else if (p_shape == K_shape_capsule) d = sdCapsule(p, body * 0.6, body * 0.5);
        else d = sdCross(p, vec2(body, body * 0.34), round);

        // An outline is the shell of the distance field: keep points within a
        // band of the boundary instead of everything inside it.
        if (p_outline > 0.0001) d = abs(d) - p_outline * 0.5;

        // A hard silhouette uses one real texel of antialiasing. A soft
        // silhouette keeps the legacy one-pixel profile contribution on the
        // fixed authored lattice so its falloff is a material feature rather
        // than a function of document resolution.
        float aaPx = max(uTexel.x, 1e-5);
        float authoredPx = pixelUnit().x;
        float v;
        if (p_falloff > 0.0001) {
          // Distance-based falloff reads as a dome, which is what height maps want.
          v = saturate(-d / (p_falloff * p_size + authoredPx));
          if (p_falloffCurve == K_falloffCurve_smooth) {
            v = smoothstep(0.0, 1.0, v);
          } else if (p_falloffCurve == K_falloffCurve_sphere) {
            v = sqrt(max(1.0 - (1.0 - v) * (1.0 - v), 0.0));
          } else if (p_falloffCurve == K_falloffCurve_custom) {
            // The profile is baked to a 256x1 lookup, so an arbitrary
            // cross-section costs exactly one tap — same as the presets.
            v = texture(p_falloffProfile, vec2(v, 0.5)).r;
          }
        } else {
          v = aaStep(d, aaPx);
        }
        if (p_invert) v = 1.0 - v;
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'gen.checker',
    label: 'Checker',
    category: 'generator',
    description: 'A checkerboard.',
    guidance:
      'Mostly a test pattern and a UV sanity check, but also a fast mask for alternating tiles.',
    keywords: ['checkerboard', 'grid', 'test', 'uv'],
    inputs: [],
    output: 'grayscale',
    params: [
      { name: 'tiles', label: 'Tiles', type: 'int', default: 8, min: 1, max: 64 },
      {
        name: 'tilesY',
        label: 'Tiles Y',
        type: 'int',
        default: 0,
        min: 0,
        max: 64,
        help: 'Separate vertical count. 0 keeps the squares square.',
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'vec2',
        default: [0, 0],
        min: -1,
        max: 1,
        step: 0.001,
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 counts = vec2(float(p_tiles), float(p_tilesY > 0 ? p_tilesY : p_tiles));
        vec2 c = floor((uv + p_offset) * counts);
        float v = mod(c.x + c.y, 2.0);
        if (p_invert) v = 1.0 - v;
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'gen.brick',
    label: 'Brick',
    category: 'generator',
    description: 'Running-bond brick or tile pattern with mortar gaps and bevel.',
    guidance:
      'A complete masonry base in one node. The output is a height map: bricks sit proud, mortar sits low. Set Row Offset to 0 for a stack bond, 0.5 for a classic running bond.',
    keywords: ['bricks', 'tiles', 'wall', 'masonry', 'mortar', 'pavers', 'stone'],
    inputs: [],
    output: 'grayscale',
    params: [
      { name: 'columns', label: 'Columns', type: 'int', default: 4, min: 1, max: 32 },
      { name: 'rows', label: 'Rows', type: 'int', default: 8, min: 1, max: 32 },
      {
        name: 'rowOffset',
        label: 'Row Offset',
        type: 'float',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.01,
        help: 'Horizontal shift applied to alternating rows. An odd count cannot alternate, so the shift accumulates instead and snaps to whole cells across the tile to stay seamless — which is why it steps.',
      },
      {
        name: 'gap',
        label: 'Mortar Gap',
        type: 'float',
        default: 0.06,
        min: 0,
        max: 0.45,
        step: 0.001,
      },
      {
        name: 'bevel',
        label: 'Bevel',
        type: 'float',
        default: 0.15,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Shape',
        help: 'Rounds the brick edge into the mortar.',
      },
      {
        name: 'roundness',
        label: 'Roundness',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Shape',
      },
      {
        name: 'pattern',
        label: 'Bond',
        type: 'enum',
        default: 'running',
        options: [
          { value: 'running', label: 'Running (offset rows)' },
          { value: 'stack', label: 'Stack (aligned)' },
          { value: 'basket', label: 'Basketweave' },
        ],
        help: 'Basketweave alternates horizontal and vertical pairs, so it ignores Row Offset.',
      },
      {
        name: 'jitter',
        label: 'Position Jitter',
        type: 'float',
        default: 0,
        min: 0,
        max: 0.5,
        step: 0.001,
        group: 'Variation',
        help: 'Nudges each brick off the grid. A little goes a long way toward hand-laid.',
      },
      {
        name: 'variation',
        label: 'Height Variation',
        type: 'float',
        default: 0.15,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
        help: 'Random per-brick height offset. Sells the hand-laid look.',
      },
      {
        name: 'seed',
        label: 'Seed',
        type: 'int',
        default: 0,
        min: 0,
        max: 9999,
        group: 'Variation',
      },
    ],
    shader: /* glsl */ `
      /**
       * Resolve the brick cell containing a point.
       *
       * Every bond reduces to the same three things: which cell we are in, the
       * position within it normalised to [-0.5,0.5], and how large that cell is
       * in UV. Keeping the size explicit is what lets the mortar gap and the
       * bevel stay visually uniform even when cells are not square — a brick
       * twice as wide as it is tall needs proportionally less of its *local*
       * width removed to leave the same gap on screen.
       */
      void brickCell(vec2 uv, float cols, float rows, out vec2 id, out vec2 f, out vec2 cellSize) {
        if (p_pattern == K_pattern_basket) {
          // Square blocks, each holding two bricks laid at right angles to the
          // neighbouring block. That alternation is the whole pattern.
          vec2 blocks = vec2(cols, cols);
          vec2 b = uv * blocks;
          vec2 bid = floor(b);
          vec2 lb = fract(b);
          bool vertical = mod(bid.x + bid.y, 2.0) > 0.5;

          vec2 cell = vertical ? vec2(lb.x * 2.0, lb.y) : vec2(lb.x, lb.y * 2.0);
          id = bid * 2.0 + floor(cell);
          f = fract(cell) - 0.5;
          cellSize = (vertical ? vec2(0.5, 1.0) : vec2(1.0, 0.5)) / blocks;
          return;
        }

        // The shift accumulates *per row* rather than alternating between two
        // states. Alternating only wraps when the row count is even — row 0 and
        // the row above the last would disagree otherwise, leaving a seam. A
        // progressive shift snapped to a whole number of cells over the full
        // height closes for any row count, and reduces to the familiar
        // alternating bond when the offset is half a cell and the rows are even.
        float row = floor(uv.y * rows);
        float shift =
          p_pattern == K_pattern_running ? staggerShift(row, p_rowOffset, rows) : 0.0;
        vec2 cell = vec2(uv.x * cols + shift, uv.y * rows);
        id = floor(cell);
        f = fract(cell) - 0.5;
        cellSize = 1.0 / vec2(cols, rows);
      }

      vec4 texdir(vec2 uv) {
        float cols = float(p_columns);
        float rows = float(p_rows);

        vec2 id;
        vec2 f;
        vec2 cellSize;
        brickCell(uv, cols, rows, id, f, cellSize);

        if (p_jitter > 0.0001) {
          vec2 wobble = hash22(mod(id, vec2(cols, rows)) + float(p_seed) * 5.9) - 0.5;
          f -= wobble * p_jitter;
        }

        // Convert a UV-space gap into local units per axis, so the mortar is the
        // same thickness horizontally and vertically whatever the cell's aspect.
        float reference = min(cellSize.x, cellSize.y);
        vec2 halfSize = vec2(0.5) - 0.5 * p_gap * reference / cellSize;
        halfSize = max(halfSize, vec2(0.01));

        float d = sdBox(f, halfSize - p_roundness * 0.5) - p_roundness * 0.5;
        // Preserve the authored one-reference-pixel edge profile without tying
        // the bevel's material width to the current framebuffer texel size.
        float authoredPx = max(pixelUnit().x / reference, 1e-5);
        float edge = p_bevel * 0.5 + authoredPx;
        float v = saturate(-d / edge);
        v = smoothstep(0.0, 1.0, v);

        float rnd = hash21(mod(id, vec2(cols, rows)) + float(p_seed) * 13.7);
        v *= 1.0 - p_variation * rnd;

        return vec4(vec3(saturate(v)), 1.0);
      }
    `,
  },

  {
    id: 'gen.wave',
    label: 'Wave',
    category: 'generator',
    description: 'Sine, triangle, saw, or square stripes.',
    guidance:
      'Directional detail: fabric weave, corrugation, brushed metal, ridges. Combine two Waves at 90° with a Blend to get a woven grid.',
    keywords: ['stripes', 'lines', 'sine', 'corrugated', 'weave', 'ridges'],
    inputs: [],
    output: 'grayscale',
    params: [
      {
        name: 'wave',
        label: 'Wave',
        type: 'enum',
        default: 'sine',
        options: [
          { value: 'sine', label: 'Sine' },
          { value: 'triangle', label: 'Triangle' },
          { value: 'saw', label: 'Saw' },
          { value: 'square', label: 'Square' },
        ],
      },
      { name: 'frequency', label: 'Frequency', type: 'int', default: 8, min: 1, max: 128 },
      {
        name: 'rotation',
        label: 'Rotation',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
      },
      { name: 'phase', label: 'Phase', type: 'float', default: 0, min: -1, max: 1, step: 0.001 },
      {
        name: 'amplitude',
        label: 'Amplitude',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.001,
        group: 'Output',
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Output',
      },
      {
        name: 'crossFrequency',
        label: 'Cross Frequency',
        type: 'int',
        default: 0,
        min: 0,
        max: 128,
        group: 'Shape',
        help: 'Adds a second wave at right angles, multiplied in. Turns stripes into a weave or grid. 0 disables it.',
      },
      {
        name: 'duty',
        label: 'Duty',
        type: 'float',
        default: 0.5,
        min: 0.01,
        max: 0.99,
        step: 0.01,
        visibleWhen: (p) => p.wave === 'square',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    shader: /* glsl */ `
      /** One cycle of the selected waveform, from a phase in 0..1. */
      float waveAt(float t) {
        if (p_wave == K_wave_sine) return sin(t * TAU) * 0.5 + 0.5;
        if (p_wave == K_wave_triangle) return 1.0 - abs(t * 2.0 - 1.0);
        if (p_wave == K_wave_saw) return t;
        return step(t, p_duty);
      }

      vec4 texdir(vec2 uv) {
        // Rotate about the tile centre so the pattern stays registered.
        vec2 p = rot(p_rotation * TAU) * (uv - 0.5) + 0.5;
        float v = waveAt(fract(p.x * float(p_frequency) + p_phase));
        if (p_crossFrequency > 0) {
          v *= waveAt(fract(p.y * float(p_crossFrequency) + p_phase));
        }
        v = v * p_amplitude + p_offset;
        if (p_invert) v = 1.0 - v;
        return vec4(vec3(saturate(v)), 1.0);
      }
    `,
  },
];
